import { CreditCard, Mail, Phone, User, X, MapPin } from "lucide-react";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { MdOutlineMapsHomeWork } from "react-icons/md";
import { GrGallery } from "react-icons/gr";
import axios from "axios";
import UploadImg from "../../components/Cloudinary/UploadImg";

const AddConsultantModal = ({ onClose, onSubmit }) => {
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const [formData, setFormData] = useState({
    consultantName: "",
    phone: "",
    email: "",
    companyName: "",
    companyRegistrationProof: null,
    country: "",
    aadharCard: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "companyRegistrationProof") {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      let imageUrl = "";

      if (formData.companyRegistrationProof instanceof File) {
        imageUrl = await UploadImg(formData.companyRegistrationProof);
      }

      const data = {
        consultantName: formData.consultantName,
        phone: formData.phone,
        email: formData.email,
        companyName: formData.companyName,
        companyRegistrationProof: imageUrl,
        country: formData.country,
        aadharCard: formData.aadharCard,
        message: formData.message,
      };

      console.log("Submitting data:", data);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/consultants/apply-consultant`,
        data,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      setSuccess("Consultant added successfully!");
      if (onSubmit) onSubmit(response.data);
      onClose();
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        err.response?.data?.message || "Something went wrong. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="max-w-3xl w-full mx-auto bg-white shadow-xl rounded-xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="py-1 bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] shadow-lg relative">
            <p className="text-center text-xl font-normal p-2 text-white">
              Add Consultant
            </p>
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>

          {success && (
            <div className="p-3 bg-green-100 text-green-700 text-sm text-center">
              {success}
            </div>
          )}
          {error && (
            <div className="p-3 bg-red-100 text-red-700 text-sm text-center">
              {error}
            </div>
          )}

          <div className="p-6 md:p-8 space-y-6">
            {/* Consultant Name + Company Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Full Name
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <User size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="consultantName"
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                    placeholder="Enter Your Name"
                    required
                    value={formData.consultantName}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Company Name
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <MdOutlineMapsHomeWork size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="companyName"
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                    placeholder="Company Name"
                    required
                    value={formData.companyName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Email + Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Email
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <Mail size={18} className="text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                    placeholder="Email Address"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Phone
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <Phone size={18} className="text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                    placeholder="Mobile Number"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            {/* Country + Aadhar */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Country
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <MapPin size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Enter Country"
                    required
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Aadhar Card Number (Optional)
                </label>
                <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                  <CreditCard size={18} className="text-gray-400" />
                  <input
                    type="text"
                    name="aadharCard"
                    value={formData.aadharCard}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      if (value.length > 12) value = value.slice(0, 12);
                      value = value.replace(/(\d{4})(?=\d)/g, "$1 ");
                      setFormData((prev) => ({ ...prev, aadharCard: value }));
                    }}
                    placeholder="1234 5678 9012"
                    className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-500 text-sm md:text-base"
                  />
                </div>
              </div>
            </div>

            {/* Company Registration Proof */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Company Registration Proof
              </label>
              <div className="rounded-xl bg-gray-50 px-4 py-3 flex items-center gap-3 border">
                <GrGallery size={18} className="text-gray-400" />
                <input
                  type="file"
                  name="companyRegistrationProof"
                  accept="image/*"
                  className="bg-transparent outline-none w-full text-gray-700 text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-1">
                Message (Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your message"
                rows={4}
                className="w-full rounded-xl bg-gray-50 px-4 py-3 border outline-none text-sm md:text-base resize-none placeholder-gray-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gradient-to-r from-[#333333] to-[#cccccc] hover:from-[#cccccc] hover:to-[#333333] text-white font-medium py-2 rounded-lg text-sm shadow-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-[#0070a8] to-[#023955] hover:from-[#1e8cbf] hover:to-[#1674a3] text-white font-medium py-2 rounded-lg text-sm shadow-md"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddConsultantModal;
